import { StateService } from '@uirouter/angular';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UiService } from '../../../core/services/core.ui.service';
import { GfTitleService } from '../../../core/services/core.title.service';
import { HttpClient } from '@angular/common/http';
import { ScmService } from '../services/scm.service'
import { normalizeCommitish } from '../services/scm.utils';
import * as _ from 'lodash';
import { doGetPromise } from '../../../core/config/core.httpclient-utils';

const IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'gif', 'bmp', 'svg', 'webp', 'ico', 'tif', 'tiff'];
// Raster formats only -- never add SVG here (see loadBinaryPreview)
const PREVIEW_IMAGE_MIME_TYPES: { [ext: string]: string } = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  bmp: 'image/bmp',
  webp: 'image/webp',
  ico: 'image/x-icon',
  tif: 'image/tiff',
  tiff: 'image/tiff'
};
const FILETYPE_ICON_PATH = '/core/img/filetypes/128/';

@Component({
  selector: 'scm-file-view',
  template: require('./scm.file.component.html')
})
export class ScmFileViewComponent implements OnInit, OnDestroy {
  @Input() project: any;
  @Input() scm: any;
  @Input() projectMenu: any;

  viewType = 'code';
  fileData: any = false;
  diffViewer = {
    selectedCommit: null,
    loading: false,
    rel: false
  };
  commitish: string = '';
  commitishLabel: string;
  lfsDownloadUrl: string = '';
  isLfsImage: boolean = false;
  isImageBinary: boolean = false;
  binaryIconUrl: string = '';
  binaryLoading: boolean = false;
  binaryObjectUrl: string = '';

  constructor(
    private uiService: UiService,
    private http: HttpClient,
    private scmService: ScmService,
    private stateService: StateService,
    private titleService: GfTitleService
  ) {}

  ngOnInit(): void {
    const stateParams = this.stateService.params;
    const isSvn = this.scm.scmType === 'svn';
    const hasParams = !!(stateParams.branch || stateParams.tag || stateParams.commit);

    if (isSvn && !hasParams) {
      this.commitish = 'HEAD';
    } else {
      this.commitish = normalizeCommitish(
        stateParams['branch'] ||
        stateParams['tag'] ||
        stateParams['commit'] ||
        this.scm.defaultBranchName
      );
    }
    this.commitishLabel = this.getLabel(stateParams);
    this.titleService.setTitle(this.project.name, _.startCase(this.scm.scmType) + ' File');

    const url = `/api/scm/${stateParams['unixName']}/file/${decodeURIComponent(stateParams['path'])}`;
    const params: any = {
      rel: 'raw,commit,history',
    }

    if (this.scm.scmType === 'git') {
      params.rel = 'raw,commit,history,blame';
      params.branch = this.commitish;
    }

    doGetPromise(
      this.http,
      url,
      params
    ).then(
      (result: any) => {
        const fileData = result;
        const txt = document.createElement('textarea');
        txt.innerHTML = _.get(fileData, 'rel.raw', '');
        _.set(fileData, 'rel.raw', txt.value);

        // Sort by commit date
        fileData.rel.history = _.map(fileData?.rel?.history, (o: any) => {
          o.commitDateObj = new Date(o.commitDate);
          return o;
        });
        this.fileData = fileData;

        if (fileData.isLfs && fileData.lfsOid) {
          this.lfsDownloadUrl = `/api/scm/${stateParams['unixName']}/lfs?oid=${fileData.lfsOid}`;
          this.isLfsImage = IMAGE_EXTENSIONS.indexOf(this.getExtension(fileData.name)) !== -1;
        }

        this.diffViewer.selectedCommit = _.get(fileData, 'rel.history[0]', {});
        _.set(this.diffViewer, 'selectedCommit.rel.isBinary', _.get(fileData, 'isBinary', false));

        if (this.isBinaryFile()) {
          const ext = this.getExtension(fileData.name);
          this.isImageBinary = !!PREVIEW_IMAGE_MIME_TYPES[ext];
          this.binaryIconUrl = FILETYPE_ICON_PATH + (ext ? ext.toUpperCase() : 'BLANK') + '.png';
          if (this.isImageBinary) {
            this.loadBinaryPreview();
          }
        } else {
          this.showDiff();
        }
      },
      () => {
        this.uiService.message.showWarning(
          `The file ${stateParams['path']} does not exist in this ${this.commitishLabel}`,
          'No Data Available'
        );
        this.fileData = { rel: false };
      }
    );
  }

  selectCommit(commit: any): void {
    if (_.get(this.diffViewer, 'selectedCommit.commitHash', '') !== _.get(commit, 'commitHash', '')) {
      this.diffViewer.selectedCommit = commit;
      if (this.isBinaryFile()) {
        if (this.isImageBinary) {
          this.loadBinaryPreview();
        }
      } else {
        this.showDiff();
      }
    }
  }

  ngOnDestroy(): void {
    this.revokeBinaryPreview();
  }

  isBinaryFile(): boolean {
    return !!(this.fileData && this.fileData.isBinary && !this.fileData.isLfs);
  }

  /**
   * Raw content URL for this file at the selected commit, or at the current commitish if none is selected.
   */
  getRawUrl(): string {
    const params = this.stateService.params;
    const commitHash = _.get(this.diffViewer, 'selectedCommit.commitHash', '');
    const query = commitHash
      ? `commit=${encodeURIComponent(commitHash)}`
      : `branch=${encodeURIComponent(this.commitish)}`;
    return `/api/scm/${params['unixName']}/raw/${decodeURIComponent(params['path'])}?${query}`;
  }

  /**
   * Fetches the image through HttpClient (so the GFAuthToken header is sent) and shows it via an object URL.
   * The blob type always comes from PREVIEW_IMAGE_MIME_TYPES, which deliberately excludes SVG: a blob URL
   * carries our origin, so an SVG blob opened in its own tab could run script.
   */
  loadBinaryPreview(): void {
    this.revokeBinaryPreview();
    const mime = PREVIEW_IMAGE_MIME_TYPES[this.getExtension(this.fileData.name)];
    if (!mime) {
      return;
    }
    this.binaryLoading = true;
    const url = this.getRawUrl();
    this.http.get(url, { responseType: 'blob' }).subscribe(
      (blob: Blob) => {
        // Drop responses for a commit that is no longer selected
        if (url !== this.getRawUrl()) {
          return;
        }
        this.binaryObjectUrl = window.URL.createObjectURL(new Blob([blob], { type: mime }));
        this.binaryLoading = false;
      },
      (err) => {
        console.error('Binary preview failed', err);
        if (url === this.getRawUrl()) {
          this.binaryLoading = false;
        }
      }
    );
  }

  onBinaryIconError(): void {
    this.binaryIconUrl = FILETYPE_ICON_PATH + 'BLANK.png';
  }

  showDiff(): void {
    const commit = _.get(this.diffViewer, 'selectedCommit.commitHash', '');
    if (commit !== '') {
      if (_.get(commit, 'rel.diff', false) === false) {
        this.diffViewer.loading = true;
        this.scmService
          .commitDiff(this.stateService.params.unixName, commit, {
            module: this.stateService.params.path,
            rel: 'diff'
          })
          .then(
            (data: any) => {
              if (data) {
                const fileDiff = _.get(data, 'rel.diff', []);
                const branch = encodeURIComponent(this.commitish);
                let querystring =
                  this.scm.scmType === 'svn' || this.scm.scmType === 'cvs'
                    ? `commit=${commit}&rel=raw`
                    : `commit=${commit}&branch=${branch}&rel=raw`;

                const rawUrl = `/api/scm/${this.stateService.params.unixName}/file/${decodeURIComponent(
                  this.stateService.params.path
                )}?${querystring}`;
                this.http.get(rawUrl).subscribe(
                  (response: any) => {
                    const fileProperties = response;
                    this.diffViewer.selectedCommit.rel = {
                      diff: fileDiff,
                      raw: fileProperties.isBinary ? fileProperties.raw : fileProperties.rel.raw,
                      isBinary: fileProperties.isBinary
                    };
                  },
                  (result) => {
                    console.log(result);
                  }
                );
              } else {
                this.diffViewer.selectedCommit.rel = {
                  diff: '',
                  raw: '',
                  isBinary: false
                };
              }
              this.diffViewer.loading = false;
            },
            (response: any) => {
              this.uiService.message.showWarning(
                `The specified commit (${commit}) does not exist in your project (${this.stateService.params.unixName})`,
                'Not Found'
              );
              this.diffViewer.selectedCommit.rel = {
                diff: '',
                raw: '',
                isBinary: false
              };
              this.diffViewer.loading = false;
            }
          );
      }
    }
  }

  downloadFile(downloadUrl: string): void {
    this.http.get(downloadUrl, { responseType: 'blob' }).subscribe(
      (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = this.fileData.name || 'download';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      },
      (err) => {
        console.error('Download failed', err);
        this.uiService.message.showWarning('Failed to download file', 'Download Error');
      }
    );
  }

  private revokeBinaryPreview(): void {
    if (this.binaryObjectUrl) {
      window.URL.revokeObjectURL(this.binaryObjectUrl);
      this.binaryObjectUrl = '';
    }
  }

  private getExtension(name: string): string {
    const parts = (name || '').split('.');
    return parts.length > 1 ? parts.pop()!.toLowerCase() : '';
  }

  private getLabel(stateParams: any): string {
    const isSvn = this.scm?.scmType === 'svn';
    if (!stateParams['branch']) {
      if (stateParams['commit']) {
        return isSvn ? 'Revision' : 'Commit';
      }
      if (stateParams['tag']) {
        return 'Tag';
      }
      if (isSvn) {
        return 'Revision';
      }
    }
    return 'Branch';
  }
}
